import React, { useState } from "react";
import { ExpandMore as ChevronDown, ChevronRight, Check, Close as X, Edit as Pencil, Add as Plus, DeleteOutline as TrashIcon } from "@mui/icons-material";
import { Box, Typography, IconButton, TextField, Collapse, MenuItem } from "@mui/material";

export const JsonNode = ({
  label,
  value,
  onChange,
  onDelete,
  rootMode = false
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editVal, setEditVal] = useState("");

  const [isAddingChild, setIsAddingChild] = useState(false);
  const [newChildKey, setNewChildKey] = useState("");
  const [newChildType, setNewChildType] = useState("String");
  const [newChildVal, setNewChildVal] = useState("");

  const isPrimitive = value === null || typeof value !== "object";
  const isArray = Array.isArray(value);

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setEditVal(String(value ?? ""));
    setIsEditing(true);
  };

  const handleSave = (e) => {
    if (e) e.stopPropagation();
    let finalVal = editVal.trim();
    if (typeof value === "boolean") {
      finalVal = editVal.toLowerCase() === "true";
    } else if (typeof value === "number") {
      const parsed = Number(editVal);
      if (!isNaN(parsed)) {
        finalVal = parsed;
      }
    }
    onChange(finalVal);
    setIsEditing(false);
  };

  const handleCancel = (e) => {
    if (e) e.stopPropagation();
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleObjectChildChange = (key, newVal) => {
    onChange({
      ...value,
      [key]: newVal
    });
  };

  const handleArrayChildChange = (index, newVal) => {
    const newArr = [...value];
    newArr[index] = newVal;
    onChange(newArr);
  };

  const handleStartAddChild = (e) => {
    e.stopPropagation();
    setIsAddingChild(true);
    setNewChildKey("");
    setNewChildType("String");
    setNewChildVal("");
    setIsExpanded(true);
  };

  const handleCancelChild = (e) => {
    if (e) e.stopPropagation();
    setIsAddingChild(false);
  };

  const handleSaveChild = (e) => {
    if (e) e.stopPropagation();
    let finalVal = newChildVal;
    if (newChildType === 'Boolean') {
      finalVal = newChildVal.toLowerCase() === 'true';
    } else if (newChildType === 'Number') {
      finalVal = Number(newChildVal);
    } else if (newChildType === 'Object') {
      finalVal = {};
    } else if (newChildType === 'Array') {
      finalVal = [];
    }

    if (isArray) {
      const newArr = [...value, finalVal];
      onChange(newArr);
    } else {
      const key = newChildKey.trim();
      if (!key) return; // need a key for object
      onChange({
        ...value,
        [key]: finalVal
      });
    }
    setIsAddingChild(false);
  };

  if (isPrimitive) {
    if (isEditing) {
      return (
        <Box className="json-primitive-edit-inline" onClick={(e) => e.stopPropagation()} sx={{ pl: 2, py: 0.5 }}>
          <Box className="json-primitive-row editing" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" className="json-primitive-label" sx={{ minWidth: 120, fontWeight: 'medium' }}>{label}:</Typography>
            <Box className="json-primitive-input-wrapper" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
              <TextField
                size="small"
                variant="standard"
                value={editVal}
                onChange={(e) => setEditVal(e.target.value)}
                onKeyDown={handleKeyDown}
                className="json-primitive-input"
                autoFocus
                sx={{ flex: 1 }}
              />
              <IconButton onClick={handleSave} className="json-primitive-edit-btn-action save" color="primary" size="small">
                <Check fontSize="small" />
              </IconButton>
              <IconButton onClick={handleCancel} className="json-primitive-edit-btn-action cancel" color="error" size="small">
                <X fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      );
    }
    return (
      <Box
        className="json-primitive-row interactive"
        onClick={handleStartEdit}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: 2,
          py: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
          cursor: 'pointer'
        }}
      >
        <Typography variant="body2" className="json-primitive-label" sx={{ minWidth: 120, fontWeight: 'medium' }}>{label}:</Typography>
        <Box className="json-primitive-value-wrapper" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Typography variant="body2" className="json-primitive-value" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
            {String(value ?? "null")}
          </Typography>
          <IconButton
            className="json-primitive-edit-pencil-btn"
            onClick={handleStartEdit}
            title="Edit Property"
            size="small"
            sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
          >
            <Pencil fontSize="small" />
          </IconButton>
          {onDelete && (
            <IconButton
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              title="Remove Property"
              size="small"
              color="error"
              sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
            >
              <TrashIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  }

  const childKeys = isArray ? value.map((_, idx) => idx) : Object.keys(value);

  if (rootMode) {
    return (
      <Box className="json-tree-children-root">
        {childKeys.map((key) => {
          const childVal = isArray ? value[key] : value[key];
          const handleChange = (newVal) => {
            if (isArray) {
              handleArrayChildChange(key, newVal);
            } else {
              handleObjectChildChange(key, newVal);
            }
          };
          const handleDelete = () => {
            if (isArray) {
              const newArr = [...value];
              newArr.splice(key, 1);
              onChange(newArr);
            } else {
              const newObj = { ...value };
              delete newObj[key];
              onChange(newObj);
            }
          };
          return (
            <JsonNode
              key={String(key)}
              label={String(key)}
              value={childVal}
              onChange={handleChange}
              onDelete={handleDelete}
            />
          );
        })}
      </Box>
    );
  }

  const itemsCount = isArray ? value.length : Object.keys(value).length;
  const infoLabel = isArray ? `[${itemsCount}]` : `{${itemsCount}}`;

  return (
    <Box className="json-tree-node">
      <Box
        className="json-tree-header"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          py: 0.5,
          '&:hover': { bgcolor: 'action.hover' }
        }}
      >
        <IconButton size="small" sx={{ p: 0.5, mr: 0.5 }}>
          {isExpanded ? <ChevronDown fontSize="small" /> : <ChevronRight fontSize="small" />}
        </IconButton>
        <Typography variant="body2" className="json-tree-label" sx={{ fontWeight: 'medium' }}>{label}</Typography>
        <Typography variant="caption" className="json-tree-info" sx={{ ml: 1, color: 'text.secondary', fontFamily: 'monospace' }}>
          {infoLabel}
        </Typography>
        {!rootMode && (
          <IconButton
            size="small"
            onClick={handleStartAddChild}
            sx={{ ml: 1, opacity: 0.5, '&:hover': { opacity: 1 } }}
            title={isArray ? "Add Value" : "Add Property"}
          >
            <Plus fontSize="small" />
          </IconButton>
        )}
        {!rootMode && onDelete && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
            color="error"
            title="Remove"
          >
            <TrashIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Collapse in={isExpanded}>
        <Box className="json-tree-children" sx={{ pl: 2, borderLeft: '1px solid', borderColor: 'divider', ml: 1.5 }}>
          {childKeys.map((key) => {
            const childVal = isArray ? value[key] : value[key];
            const handleChange = (newVal) => {
              if (isArray) {
                handleArrayChildChange(key, newVal);
              } else {
                handleObjectChildChange(key, newVal);
              }
            };
            const handleDelete = () => {
              if (isArray) {
                const newArr = [...value];
                newArr.splice(key, 1);
                onChange(newArr);
              } else {
                const newObj = { ...value };
                delete newObj[key];
                onChange(newObj);
              }
            };
            return (
              <JsonNode
                key={String(key)}
                label={String(key)}
                value={childVal}
                onChange={handleChange}
                onDelete={handleDelete}
              />
            );
          })}

          {isAddingChild && (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1 }} onClick={(e) => e.stopPropagation()}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                {isArray ? "Add Value" : "Add Property"}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {!isArray && (
                  <TextField
                    size="small"
                    placeholder="Key"
                    value={newChildKey}
                    onChange={e => setNewChildKey(e.target.value)}
                    sx={{ flex: 1, minWidth: 100 }}
                  />
                )}
                {newChildType !== 'Object' && newChildType !== 'Array' && (
                  <TextField
                    size="small"
                    placeholder="Value"
                    value={newChildVal}
                    onChange={e => setNewChildVal(e.target.value)}
                    sx={{ flex: 1, minWidth: 100 }}
                  />
                )}
                <TextField
                  select
                  size="small"
                  value={newChildType}
                  onChange={e => setNewChildType(e.target.value)}
                  sx={{ width: 100 }}
                >
                  <MenuItem value="String">String</MenuItem>
                  <MenuItem value="Number">Number</MenuItem>
                  <MenuItem value="Boolean">Boolean</MenuItem>
                  <MenuItem value="Array">Array</MenuItem>
                  <MenuItem value="Object">Object</MenuItem>
                </TextField>
                <IconButton color="primary" onClick={handleSaveChild} size="small" sx={{ bgcolor: 'background.paper', ml: 1 }}>
                  <Check fontSize="small" />
                </IconButton>
                <IconButton color="error" onClick={handleCancelChild} size="small" sx={{ bgcolor: 'background.paper' }}>
                  <X fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default JsonNode;
